import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Checkbox } from '@kcm/ui'
import { ErrorCode } from '@kcm/contracts'
import { useTranslate } from '@tolgee/react'
import { useSite } from '../components/SiteProvider'
import { ApiError } from '../lib/api'
import { endpoints } from '../lib/endpoints'
import { useGoToCurrentStep } from '../hooks/useResume'
import { useFunnel } from '../store/funnel'

/**
 * Step 4 — the legal acceptance and the handoff (PLAN.md §13.4, §16).
 *
 * Two things happen here, in this order and no other:
 *
 *   1. **Each company document is accepted individually.** The CRM records an
 *      acceptance per document, and the audit trail is worth nothing if the
 *      person could not have said what they accepted. "Accept all" is a
 *      convenience over that list, not a substitute for it: every document
 *      stays on screen with its own box and its own version, and what goes to
 *      the CRM is still one acceptance per id.
 *   2. **The CRM mints a single-use `direct_login` URL** and the browser
 *      follows it, so nobody retypes a password they set twenty minutes ago.
 *
 * That URL is the sharp edge. In the legacy funnel `step5.php` handed one to
 * anyone who posted a live application's email address, which made it an
 * account-takeover primitive (finding #2). Here it is minted only by
 * `POST /onboarding/complete`, only for the session that owns the application,
 * and only once that application is complete — so this screen never asks for an
 * identifier, and there is nothing on it to guess.
 */
export function StepDone() {
  const navigate = useNavigate()
  const { t } = useTranslate()
  const goToCurrentStep = useGoToCurrentStep()
  const [accepted, setAccepted] = useState<number[]>([])
  // Collapsed by default: nineteen rows of near-identical legalese is a wall,
  // and §11 says walls are where people leave. The documents are one tap away
  // and each keeps its own box — collapsing changes what is on screen, not
  // what is recorded, which is still one acceptance per document id.
  const [expanded, setExpanded] = useState(false)

  const docs = useQuery({
    queryKey: ['legal-docs'],
    queryFn: ({ signal }) => endpoints.legalDocs(signal),
    staleTime: 5 * 60 * 1000,
  })

  const complete = useMutation({
    mutationFn: () => endpoints.complete({ acceptedDocumentIds: accepted }),
    onSuccess: (res) => {
      // The server has already destroyed the session and cleared its cookie.
      // This clears the half of it that lives in this tab, so navigating back
      // to the funnel offers a blank step 1 rather than a spent OTP challenge.
      useFunnel.getState().reset()
      // A full navigation, not a router push: the destination is the client
      // cabinet, and the URL is spent the moment it is followed.
      window.location.assign(res.redirectUrl)
    },
  })

  const loadError = docs.error instanceof ApiError ? docs.error : null
  const completeError = complete.error instanceof ApiError ? complete.error : null

  const blocked = loadError ?? completeError
  if (blocked?.code === ErrorCode.STEP_NOT_AVAILABLE) {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="info" title={t('gate.stepToFinishTitle')}>
          {blocked.message}
        </Alert>
        <Button onClick={goToCurrentStep}>{t('gate.continueWhereLeft')}</Button>
      </div>
    )
  }

  if (docs.isPending) {
    return (
      <p role="status" className="text-sm text-muted">
        {t('done.loadingDocs')}
      </p>
    )
  }

  if (!docs.data) {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="error" title={t('done.loadFailedTitle')}>
          {loadError?.message ?? t('done.tryAgainMoment')}
        </Alert>
        <Button onClick={() => void docs.refetch()}>{t('common.tryAgain')}</Button>
        <SupportLine />
      </div>
    )
  }

  const documents = docs.data.documents
  const outstanding = documents.filter((d) => !accepted.includes(d.id)).length

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (!complete.isPending) complete.mutate()
      }}
    >
      <div>
        <h1 className="text-xl font-bold text-brand">{t('done.title')}</h1>
        <p className="mt-1 text-sm text-muted">
          {documents.length === 0 ? t('done.readyToSubmit') : t('done.pleaseAccept')}
        </p>
      </div>

      {completeError && (
        <Alert tone="error" title={titleFor(completeError, t)}>
          {completeError.message}
        </Alert>
      )}

      {documents.length > 0 && (
        <div className="divide-y divide-line rounded-xl border border-line bg-white">
          {/* There are nineteen of these on a live site. The master tick ticks
              boxes the user can still open, read and untick — what goes to the
              CRM is unchanged: one acceptance per document id, per version. */}
          {documents.length > 1 && (
            <div className="bg-surface-alt px-4">
              <Checkbox
                label={
                  <span className="font-semibold text-ink">
                    {t('done.agreeAll', { count: documents.length })}
                  </span>
                }
                checked={outstanding === 0}
                disabled={complete.isPending}
                onChange={(e) => setAccepted(e.target.checked ? documents.map((d) => d.id) : [])}
              />
              <button
                type="button"
                className="mb-3 text-sm text-muted underline"
                aria-expanded={expanded}
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded
                  ? t('done.hideDocs')
                  : t('done.showDocs', { count: documents.length })}
              </button>
            </div>
          )}

          {(expanded || documents.length <= 1) &&
            documents.map((doc) => (
              <div key={doc.id} className="px-4">
              <Checkbox
                required
                checked={accepted.includes(doc.id)}
                disabled={complete.isPending}
                onChange={(e) =>
                  setAccepted((ids) =>
                    e.target.checked ? [...ids, doc.id] : ids.filter((id) => id !== doc.id),
                  )
                }
                label={
                  <span>
                    <span className="font-medium text-ink">{doc.name}</span>
                    {doc.version !== undefined && (
                      // "Accepted the terms" means nothing without saying which
                      // terms, so the version stays even in the compact row.
                      <span className="ml-2 text-xs text-muted">
                        {t('done.version', { version: doc.version })}
                      </span>
                    )}
                    {doc.acceptText && (
                      <span className="mt-0.5 block text-xs text-muted">
                        {fillDocumentName(doc.acceptText, doc.name)}
                      </span>
                    )}
                  </span>
                }
              />
              </div>
            ))}
        </div>
      )}

      <Button type="submit" disabled={complete.isPending || outstanding > 0}>
        {complete.isPending ? t('done.finishing') : t('done.finishSignIn')}
      </Button>

      {outstanding > 0 && (
        <p role="status" className="text-sm text-muted">
          {outstanding === 1
            ? t('done.oneLeft')
            : t('done.docsLeft', { count: outstanding })}
        </p>
      )}

      <p className="text-xs text-muted">{t('done.straightIn')}</p>

      <GetTheApps />

      <SupportLine />
    </form>
  )
}

/**
 * The trading platforms, offered at the moment they become relevant: the next
 * thing this person does is open MT5 or the app, and sending them hunting
 * through the marketing site for a download is one more place to stall.
 * Plain links styled as buttons — a download must survive popup blockers and
 * in-app browsers, which window.open does not.
 */
const APPS = [
  { label: 'Download MT5', href: 'https://www.kcmtrade.com/trade/download-kcm-mt5', icon: 'mt5' },
  {
    label: 'KCM Trade App — Android',
    href: 'https://play.google.com/store/apps/details?id=com.kcmtrade.app',
    icon: 'android',
  },
  {
    label: 'KCM Trade App — iOS',
    href: 'https://apps.apple.com/us/app/kcm-trade/id6717598984',
    icon: 'ios',
  },
] as const

function GetTheApps() {
  const { t } = useTranslate()
  return (
    <section className="rounded-xl border border-line bg-white p-5">
      <h2 className="text-sm font-semibold text-ink">{t('done.getApps')}</h2>
      <p className="mt-1 text-sm text-muted">{t('done.getAppsSubtitle')}</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        {APPS.map((app) => (
          <a
            key={app.href}
            href={app.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-line-strong px-4 text-sm font-medium text-ink hover:bg-surface-alt"
          >
            <AppIcon kind={app.icon} />
            {app.label}
          </a>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted">
        {t('done.onComputer')}{' '}
        <a
          className="underline"
          href="https://app.kcm.trade/qr/"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('done.scanQr')}
        </a>{' '}
        {t('done.scanQrSuffix')}
      </p>
    </section>
  )
}

/**
 * A recognisable mark per store, monochrome in `currentColor` so it inherits
 * the button's text colour and never bakes in a brand hex (the no-hex gate).
 */
function AppIcon({ kind }: { kind: 'mt5' | 'android' | 'ios' }) {
  if (kind === 'android') {
    return (
      <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M4 2.5v19c0 .4.4.7.8.5l16-9.5c.4-.2.4-.8 0-1L4.8 2c-.4-.2-.8.1-.8.5z" />
      </svg>
    )
  }
  if (kind === 'ios') {
    return (
      <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
      </svg>
    )
  }
  return (
    <svg
      className="h-5 w-5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 20V11M9 20V5M14 20v-7M19 20V8" />
    </svg>
  )
}

/**
 * The CRM stores its accept text with a `%document%` placeholder where the
 * document's own name belongs, and it was reaching the screen unsubstituted —
 * every row read "agree to the %document%". There is no URL to link to
 * (`LegalDocument` carries none), so the name is what goes in.
 */
function fillDocumentName(acceptText: string, name: string): string {
  return acceptText.replaceAll('%document%', name)
}

function SupportLine() {
  const site = useSite()
  const { t } = useTranslate()
  return (
    <p className="text-sm text-muted">
      {t('common.needHelp')}{' '}
      <a className="underline" href={`mailto:${site.supportEmail}`}>
        {site.supportEmail}
      </a>
    </p>
  )
}

function titleFor(error: ApiError, t: (key: string) => string): string {
  switch (error.code) {
    case ErrorCode.RATE_LIMITED:
      return t('err.tooManyAttempts')
    case ErrorCode.VALIDATION_FAILED:
      return t('done.acceptEvery')
    case ErrorCode.CRM_UNAVAILABLE:
      return t('err.temporarilyUnavailable')
    default:
      return t('done.completeFailed')
  }
}
